import { Clear } from '@mui/icons-material';
import { Button, DialogContent, type DialogProps, Paper, type PaperProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useEffect, useRef, useState, type ComponentType, type ReactElement } from 'react';
import Draggable, { type DraggableData, type DraggableEvent, type DraggableProps } from 'react-draggable';

const DraggableTyped = Draggable as unknown as ComponentType<Partial<DraggableProps>>;
import { CancelButton, SaveButton } from '@/app/shared/ui/Buttons';
import Loader from '@/app/shared/ui/Loader';
import { CloseModalIcon, ModalFooter, StyledDialog, StyledDialogTitle } from '../../styles/modal';

export const FlexEnd = styled('div')`
  display: flex;
  justify-content: flex-end;
`;

interface IAlertDialogProps extends DialogProps {
  dialogWidth?: string;
  dialogMinWidth?: string;
  dialogMaxWidth?: string;
  dialogHeight?: string;
  dialogMinHeight?: string;
  hideCloseIcon?: boolean;
  draggable?: boolean;
  hideBackdrop?: boolean;
  title?: string;
  marginTop?: string;
  showFooter?: boolean;
  hideCancel?: boolean;
  showHeaderBtn?: boolean;
  hideSave?: boolean;
  isSubmitting?: boolean;
  cancelButtonText?: string;
  buttonText?: string;
  onClose?: (event: object, reason?: string) => void;
  onConfirm?: () => void;
  onHeaderBtnClick?: () => void;
  footer?: ReactElement;
  isLoading?: boolean;
  headerButtonText?: string;
  resetPosition?: number;
}

interface IPaperProps extends PaperProps {
  resetPosition?: number;
}

const PaperComponent = ({resetPosition, ...rest}: IPaperProps) => {
  const draggableRef = useRef<HTMLDivElement>(null);
  
  // settigntop bounds so that user cant  drag beyond to view port but can drag beyond left, right and bottom viewport upto certain extent
  const [bounds, setBounds] = useState({top: 0, left: 0, bottom: 0, right: 0, });
  const [position, setPosition] = useState({x: 0, y: 0});
  
  useEffect(() => {
    // we need to set the bounds only once component mounts initally.
    if (draggableRef && draggableRef.current) {
      const {offsetLeft, offsetTop, offsetWidth}  = draggableRef.current;
      const top = -offsetTop;
      const bottom = window.innerHeight - offsetTop - 80;
      const right = window.innerWidth - offsetLeft - 80;
      const left = -(offsetLeft + offsetWidth - 80);
      setBounds({ top, right, bottom, left });
    }
  }, []);

  useEffect(() => {
    // if resetPosition attribute is coming this will reset the position of modla to it original postion when clicked on same button again
    setPosition({ x: 0, y: 0 });
  }, [resetPosition]);
  
  const handleStop = useCallback((_e: DraggableEvent, data: DraggableData) => {
    const { x, y } = data;
    setPosition({ x, y });
  }, []);

  return (
    <DraggableTyped nodeRef={draggableRef} position={position} bounds={bounds} handle="#draggable-handle" cancel=".close-modal-btn" onStop={handleStop}>
      <Paper ref={draggableRef} {...rest} />
    </DraggableTyped>
  );
};

enum CloseReasons {
  BACKDROP = 'backdropClick',
  ESCAPE = 'escapeKeyDown',
  CLOSE_ICON = 'closeIcon'
}

export const Modal = ({
  children,
  dialogWidth,
  dialogMinWidth,
  dialogMaxWidth,
  dialogHeight,
  dialogMinHeight,
  hideCloseIcon,
  onClose,
  onConfirm,
  onHeaderBtnClick,
  footer,
  isLoading = false,
  draggable = true,
  hideBackdrop = false,
  marginTop,
  showFooter = false,
  hideCancel = false,
  isSubmitting = false,
  hideSave = false,
  showHeaderBtn = false,
  cancelButtonText = 'Cancel',
  buttonText = '',
  headerButtonText = '',
  title = '',
  resetPosition = 0,
  ...props
}: IAlertDialogProps) => {

  const [isTrue, setIsTrue] = useState(false);

  useEffect(() => {
    if (resetPosition) {
      setIsTrue(true);
    }
  }, [resetPosition]);

  return (
    <StyledDialog
      disableEnforceFocus
      hideBackdrop={hideBackdrop}
      width={dialogWidth}
      minwidth={dialogMinWidth}
      maxwidth={dialogMaxWidth}
      height={dialogHeight}
      minheight={dialogMinHeight}
      PaperComponent={draggable ? PaperComponent : undefined}
      style={hideBackdrop ? { zIndex: isTrue ? '1300' : '1299', pointerEvents: 'none' } : undefined}
      PaperProps={draggable ? {
        resetPosition: resetPosition, style: { pointerEvents: 'auto' }, sx: {
          width: {
            xs: '95vw',
            sm: '90vw',
            md: '800px',
            lg: '1000px',
          },
        },
      } as IPaperProps : undefined}
      onClick={() => setIsTrue(true)}
      onBlur={() => setIsTrue(false)}
      onClose={hideBackdrop ? undefined : onClose}
      {...props}
    >
      {isLoading ? (
        <Loader />
      ) : (
        <>
          <StyledDialogTitle id='draggable-handle' $isDraggable={draggable} $isReduced={!!marginTop}>
            {title}
            <FlexEnd style={{gap: 5}}>
              {showHeaderBtn && (
                <Button 
                 size="small"
                 variant="contained"
                 sx={{height: '20px', alignSelf: 'center'}}
                 onClick={onHeaderBtnClick}
                >
                  {headerButtonText}
                </Button>
              )}

              {!hideCloseIcon && (
                  <CloseModalIcon className="close-modal-btn" onClick={(e: object) => onClose?.(e, CloseReasons.CLOSE_ICON)}>
                  <Clear />
                </CloseModalIcon>
              )}
              </FlexEnd>
          </StyledDialogTitle>

          <DialogContent style={{ marginTop, padding: '8px 20px' }}>
            {children}

            {showFooter && (
              <ModalFooter style={{ gap: 0 }}>
                {!hideCancel && (
                  <CancelButton
                    width="125px"
                    disabled={isSubmitting}
                    label={cancelButtonText}
                    onClick={onClose}
                  />
                )}

                {!hideSave && (
                  <SaveButton
                    width="125px"
                    label={buttonText || 'Confirm'}
                    isLoading={isSubmitting}
                    onClick={() => onConfirm?.()}
                  />
                )}
              </ModalFooter>
            )}
          </DialogContent>

          {footer}
        </>
      )}
    </StyledDialog>
  );
};
